import { PageBlock, StageRow } from "./pageRows.types";
import { StageConfig } from "../render/fieldConfig.types";
import { isCredentialAttribute } from "./credentialAttributes";
import {
  PageConfig,
  PageConfigBlock,
  PageConfigSectionBlock,
} from "./pageConfig.types";

// Ids exist for the designer, not the renderer, so they're dropped here.
export function sectionToStage(section: PageConfigSectionBlock): StageConfig {
  return {
    title: section.title,
    ...(section.helperText === undefined
      ? {}
      : { helperText: section.helperText }),
    ...(section.visibleWhen === undefined
      ? {}
      : { visibleWhen: section.visibleWhen }),
    ...(section.headerComponentKey === undefined
      ? {}
      : { headerComponentKey: section.headerComponentKey }),
    ...(section.bare ? { bare: true } : {}),
    ...(section.wizardStep ? { wizardStep: true } : {}),
    ...(section.wizardStep && section.wizardOrder !== undefined
      ? { wizardOrder: section.wizardOrder }
      : {}),
    fields: section.fields.map(({ id, ...field }) => field),
  };
}

// Even a view saved before they were withheld from authors cannot draw one.
const withoutCredentials = (
  section: PageConfigSectionBlock
): PageConfigSectionBlock => ({
  ...section,
  fields: section.fields.filter(
    (field) => !("name" in field && isCredentialAttribute(field.name))
  ),
});

function toBlock(block: PageConfigBlock): PageBlock {
  return block.kind === "section"
    ? { kind: "stage", stage: sectionToStage(withoutCredentials(block)) }
    : {
        kind: "tables",
        ...(block.title === undefined ? {} : { title: block.title }),
        tabs: block.tabs,
      };
}

/** The single boundary between stored page config and the runtime. */
export function configToStageRows(config: PageConfig): StageRow[] {
  return config.rows.map((row) => ({
    columns: row.columns.map((column) => ({
      span: column.span,
      // Only carried when switched on, so a column that was never touched serialises
      // exactly as it did before these existed.
      ...(column.card ? { card: true } : {}),
      ...(column.card && column.fillHeight ? { fillHeight: true } : {}),
      blocks: column.blocks.map(toBlock),
    })),
  }));
}

/** One section of a config as a stage, for a form that reuses part of another. */
export function getSectionStage(
  config: PageConfig,
  sectionId: string
): StageConfig {
  const section = config.rows
    .flatMap((row) => row.columns)
    .flatMap((column) => column.blocks)
    .find(
      (block): block is PageConfigSectionBlock =>
        block.kind === "section" && block.id === sectionId
    );

  if (!section) {
    throw new Error(`No section "${sectionId}" in ${config.entityName} config`);
  }

  return sectionToStage(section);
}
